"use client";

import { ChevronDown, ChevronUp, Download, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { useAppSelector } from "../../../redux/hooks";
import { Button } from "../../ui/button.jsx";
import { OptimizedMultiSelect, OptimizedSelect } from "../optimized-form-field";

type ReportSectionImportProps = {
  label?: string;
  reports: any[];
  subsectionOptions?: { value: string; label: string }[];
  disabled?: boolean;
  isImporting?: boolean;
  onImport: (reportId: string, selectedSubsections?: string[]) => void;
};

export function ReportSectionImport({
  label = "Export Data",
  reports,
  subsectionOptions = [],
  disabled = false,
  isImporting = false,
  onImport,
}: ReportSectionImportProps) {
  const isOnline = useAppSelector((state) => state.offline.isOnline);
  const [selectedReportId, setSelectedReportId] = useState("");
  const [selectedSubsections, setSelectedSubsections] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const selectedReport = useMemo(
    () => reports.find((item) => item?.report?._id === selectedReportId),
    [reports, selectedReportId],
  );
  const requiresSubsectionSelection = subsectionOptions.length > 0;
  const canImport =
    Boolean(selectedReport) &&
    (!requiresSubsectionSelection || selectedSubsections.length > 0);

  if (!isOnline) return null;

  return (
    <div className="sticky top-40 z-20 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-100 p-3 shadow-sm dark:border-border dark:bg-none dark:bg-surface sm:p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-start gap-2">
            <div className="mt-0.5 rounded-lg bg-blue-100 p-2 text-blue-700 dark:bg-brand/30 dark:text-link">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-slate-900 dark:text-foreground">
                {label}
              </p>
              <p className="text-xs text-slate-500 dark:text-muted-foreground">
                Pull data from a previously completed report.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="rounded-md p-2 text-slate-600 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-muted-foreground dark:hover:bg-accent sm:hidden dark:focus:ring-ring"
            aria-expanded={isExpanded}
            aria-controls="report-section-import-controls"
            aria-label={
              isExpanded ? "Hide import controls" : "Show import controls"
            }
            title={isExpanded ? "Hide import controls" : "Show import controls"}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        </div>

        <div
          id="report-section-import-controls"
          className={`${isExpanded ? "flex" : "hidden"} min-w-0 flex-col gap-2 sm:flex sm:flex-row sm:items-end lg:min-w-[420px]`}
        >
          <div className="min-w-0 flex-1">
            <OptimizedSelect
              id="export_data"
              value={selectedReportId}
              onChange={setSelectedReportId}
              placeholder={"Choose a report"}
              options={
                reports.length > 0
                  ? reports.map((item) => ({
                      value: item.report._id,
                      label:
                        `${item.report.reportName} - ${item.report.templateName
                          ?.split(/[-\s]+/)
                          .map((word) => word.charAt(0).toUpperCase())
                          .join(".")}` || "Untitled report",
                    }))
                  : [{ value: "no", label: "No Report Available" }]
              }
              readOnly={disabled || isImporting}
            />
          </div>

          {requiresSubsectionSelection && (
            <div className="min-w-0 flex-1">
              <OptimizedMultiSelect
                id="export_data_subsections"
                label=""
                values={selectedSubsections}
                onChange={setSelectedSubsections}
                placeholder="Select sections"
                options={subsectionOptions}
                readOnly={disabled || isImporting}
              />
            </div>
          )}

          <Button
            type="button"
            variant={canImport ? "default" : "outline"}
            size="sm"
            className="w-full shrink-0 sm:w-auto"
            disabled={disabled || isImporting || !canImport}
            onClick={() =>
              navigator.onLine &&
              selectedReportId &&
              onImport(selectedReportId, selectedSubsections)
            }
          >
            <Download className="mr-2 h-4 w-4" />
            {isImporting ? "Importing..." : "Import"}
          </Button>
        </div>
      </div>
    </div>
  );
}
